import api from '@pray/shared/utils/api';

import type { SeedPreviewCatalogSelection, SeedRunDetailResponse, SeedRunType } from './types';

const RUNS_URL = '/web/studio/admin/seed-emails/runs';

export type CreateSeedRunParams = {
  /**
   * One per draft. A double click and the HTTP client's own silent retry after a 401 both send the
   * same id, and the server answers the run it already created instead of starting a second one.
   */
  clientRequestId: string;
  type: SeedRunType;
  /** A campaign uuid, or a `SENDGRID_TEMPLATE_*` registry key for the transactional type. */
  campaignId: string;
  campaignName: string;
  /** Absent for the transactional type, whose template belongs to no artist. */
  artistId?: string;
  /** Required for a recurring campaign: which occurrence the run stands for. */
  occurrenceDate?: string | null;
  groupIds: string[];
  /** Seeds ticked one by one. The server unions them with the groups and deduplicates. */
  looseSeedIds: string[];
  catalogSelections?: SeedPreviewCatalogSelection[];
  replacements?: Record<string, string>;
  /** The value the wizard proposes. The server prefixes it by stage and enforces uniqueness. */
  trackingHeaderValue: string;
  customHeaders?: { name: string; value: string }[];
};

/**
 * Creates a run and hands it to the seed queue.
 *
 * The body sends the SELECTION, never the resolved audience: the server unions the groups' live
 * members with the loose picks and writes what it computed, so a group that changed between step 2
 * and Send produces one answer instead of two that disagree in silence.
 */
export async function postCreateRun({
  clientRequestId,
  type,
  campaignId,
  campaignName,
  artistId,
  occurrenceDate,
  groupIds,
  looseSeedIds,
  catalogSelections = [],
  replacements = {},
  trackingHeaderValue,
  customHeaders = [],
}: CreateSeedRunParams) {
  const res = await api.executePostRequest<{ data: SeedRunDetailResponse }>(RUNS_URL, {
    client_request_id: clientRequestId,
    type,
    campaign_id: campaignId,
    campaign_name: campaignName,
    ...(artistId ? { artist_id: artistId } : {}),
    ...(occurrenceDate ? { occurrence_date: occurrenceDate } : {}),
    group_ids: groupIds,
    loose_seed_ids: looseSeedIds,
    // snake_case happens at this boundary and nowhere above it, the way `putGroup` and
    // `postPreview` do it: a caller holding a wire-shaped object would leak that casing into the
    // draft and the query key.
    // No name travels with a selection. The server reads the record by Key during the render and
    // names it there, so the run's label describes the row that was actually sent.
    // `POST /runs` refuses any other key on this object.
    catalog_selections: catalogSelections.map((selection) => ({
      slug: selection.slug,
      catalog_id: selection.catalogId,
      key: selection.key,
    })),
    replacements,
    tracking_header_value: trackingHeaderValue,
    custom_headers: customHeaders,
  });

  return res.data.data;
}
